// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
//
// Icon assets: see libs/shared/ui-kit/AGENTS.md (Lucide → assets/icons → SVG_ICONS).
import {
  type ComponentType,
  forwardRef,
  type PropsWithoutRef,
  type SVGProps,
  useMemo,
} from 'react';
import { ReactComponent as Account } from '@shared/ui-kit/assets/icons/account.svg';
import { ReactComponent as Activity } from '@shared/ui-kit/assets/icons/activity.svg';
import { ReactComponent as Add } from '@shared/ui-kit/assets/icons/add.svg';
import { ReactComponent as Advisors } from '@shared/ui-kit/assets/icons/advisors.svg';
import { ReactComponent as AiBuilder } from '@shared/ui-kit/assets/icons/ai-builder.svg';
import { ReactComponent as AlertTriangle } from '@shared/ui-kit/assets/icons/alert-triangle.svg';
import { ReactComponent as Analysis } from '@shared/ui-kit/assets/icons/analysis.svg';
import { ReactComponent as ArrowDown } from '@shared/ui-kit/assets/icons/arrow-down.svg';
import { ReactComponent as ArrowDownUp } from '@shared/ui-kit/assets/icons/arrow-down-up.svg';
import { ReactComponent as ArrowLeft } from '@shared/ui-kit/assets/icons/arrow-left.svg';
import { ReactComponent as ArrowLeftRight } from '@shared/ui-kit/assets/icons/arrow-left-right.svg';
import { ReactComponent as ArrowRight } from '@shared/ui-kit/assets/icons/arrow-right.svg';
import { ReactComponent as ArrowUp } from '@shared/ui-kit/assets/icons/arrow-up.svg';
import { ReactComponent as Asset } from '@shared/ui-kit/assets/icons/asset.svg';
import { ReactComponent as AssetAllocation } from '@shared/ui-kit/assets/icons/asset-allocation.svg';
import { ReactComponent as Back } from '@shared/ui-kit/assets/icons/back.svg';
import { ReactComponent as Boxes } from '@shared/ui-kit/assets/icons/boxes.svg';
import { ReactComponent as BrazilFlag } from '@shared/ui-kit/assets/icons/flags/brazil.svg';
import { ReactComponent as Banknote } from '@shared/ui-kit/assets/icons/banknote.svg';
import { ReactComponent as Briefcase } from '@shared/ui-kit/assets/icons/briefcase.svg';
import { ReactComponent as Building } from '@shared/ui-kit/assets/icons/building-2.svg';
import { ReactComponent as Buy } from '@shared/ui-kit/assets/icons/buy.svg';
import { ReactComponent as Calendar } from '@shared/ui-kit/assets/icons/calendar.svg';
import { ReactComponent as CanadaFlag } from '@shared/ui-kit/assets/icons/flags/canada.svg';
import { ReactComponent as CaretDown } from '@shared/ui-kit/assets/icons/caret-down.svg';
import { ReactComponent as CaretRight } from '@shared/ui-kit/assets/icons/caret-right.svg';
import { ReactComponent as CaretUp } from '@shared/ui-kit/assets/icons/caret-up.svg';
import { ReactComponent as ChartColumn } from '@shared/ui-kit/assets/icons/chart-column.svg';
import { ReactComponent as ChartLine } from '@shared/ui-kit/assets/icons/chart-line.svg';
import { ReactComponent as ChartPie } from '@shared/ui-kit/assets/icons/chart-pie.svg';
import { ReactComponent as ChatCaretLeft } from '@shared/ui-kit/assets/icons/chat-caret-left.svg';
import { ReactComponent as ChatCaretRight } from '@shared/ui-kit/assets/icons/chat-caret-right.svg';
import { ReactComponent as Checkbox } from '@shared/ui-kit/assets/icons/checkbox.svg';
import { ReactComponent as CheckCircle } from '@shared/ui-kit/assets/icons/check-circle.svg';
import { ReactComponent as Checked } from '@shared/ui-kit/assets/icons/checked.svg';
import { ReactComponent as ChevronDown } from '@shared/ui-kit/assets/icons/chevron-down.svg';
import { ReactComponent as CircleArrowDown } from '@shared/ui-kit/assets/icons/circle-arrow-down.svg';
import { ReactComponent as CircleArrowUp } from '@shared/ui-kit/assets/icons/circle-arrow-up.svg';
import { ReactComponent as CircleCheck } from '@shared/ui-kit/assets/icons/circle-check.svg';
import { ReactComponent as ClipboardCheck } from '@shared/ui-kit/assets/icons/clipboard-check.svg';
import { ReactComponent as Clock } from '@shared/ui-kit/assets/icons/clock.svg';
import { ReactComponent as Close } from '@shared/ui-kit/assets/icons/close.svg';
import { ReactComponent as CloseOutline } from '@shared/ui-kit/assets/icons/close-outline.svg';
import { ReactComponent as CopilotChatBackButton } from '@shared/ui-kit/assets/icons/copilot-chat-back.svg';
import { ReactComponent as CopilotChatButton } from '@shared/ui-kit/assets/icons/copilot-chat.svg';
import { ReactComponent as CopilotChatSendButton } from '@shared/ui-kit/assets/icons/copilot-chat-send.svg';
import { ReactComponent as Coins } from '@shared/ui-kit/assets/icons/coins.svg';
import { ReactComponent as Copy } from '@shared/ui-kit/assets/icons/copy.svg';
import { ReactComponent as CreditCard } from '@shared/ui-kit/assets/icons/credit-card.svg';
import { ReactComponent as Dashboard } from '@shared/ui-kit/assets/icons/dashboard.svg';
import { ReactComponent as DatePicker } from '@shared/ui-kit/assets/icons/date-picker.svg';
import { ReactComponent as DollarSign } from '@shared/ui-kit/assets/icons/dollar-sign.svg';
import { ReactComponent as Download } from '@shared/ui-kit/assets/icons/download.svg';
import { ReactComponent as Eliminate } from '@shared/ui-kit/assets/icons/eliminate.svg';
import { ReactComponent as Ellipsis } from '@shared/ui-kit/assets/icons/ellipsis.svg';
import { ReactComponent as EllipsisVertical } from '@shared/ui-kit/assets/icons/ellipsis-vertical.svg';
import { ReactComponent as ETF } from '@shared/ui-kit/assets/icons/etf.svg';
import { ReactComponent as EuropeanUnionFlag } from '@shared/ui-kit/assets/icons/flags/european-union.svg';
import { ReactComponent as ExclamationMark } from '@shared/ui-kit/assets/icons/exclamation-mark.svg';
import { ReactComponent as ExternalLink } from '@shared/ui-kit/assets/icons/external-link.svg';
import { ReactComponent as Eye } from '@shared/ui-kit/assets/icons/eye.svg';
import { ReactComponent as EyeOff } from '@shared/ui-kit/assets/icons/eye-off.svg';
import { ReactComponent as FileCheck } from '@shared/ui-kit/assets/icons/file-check.svg';
import { ReactComponent as FileImage } from '@shared/ui-kit/assets/icons/file-image.svg';
import { ReactComponent as FilePen } from '@shared/ui-kit/assets/icons/file-pen.svg';
import { ReactComponent as FileText } from '@shared/ui-kit/assets/icons/file-text.svg';
import { ReactComponent as Flame } from '@shared/ui-kit/assets/icons/flame.svg';
import { ReactComponent as FranceFlag } from '@shared/ui-kit/assets/icons/flags/france.svg';
import { ReactComponent as Funnel } from '@shared/ui-kit/assets/icons/funnel.svg';
import { ReactComponent as GermanyFlag } from '@shared/ui-kit/assets/icons/flags/germany.svg';
import { ReactComponent as Globe } from '@shared/ui-kit/assets/icons/globe.svg';
import { ReactComponent as HandCoins } from '@shared/ui-kit/assets/icons/hand-coins.svg';
import { ReactComponent as Home } from '@shared/ui-kit/assets/icons/home.svg';
import { ReactComponent as IdCard } from '@shared/ui-kit/assets/icons/id-card.svg';
import { ReactComponent as Indeterminate } from '@shared/ui-kit/assets/icons/indeterminate.svg';
import { ReactComponent as Info } from '@shared/ui-kit/assets/icons/info.svg';
import { ReactComponent as Insights } from '@shared/ui-kit/assets/icons/insights.svg';
import { ReactComponent as JapanFlag } from '@shared/ui-kit/assets/icons/flags/japan.svg';
import { ReactComponent as Landmark } from '@shared/ui-kit/assets/icons/landmark.svg';
import { ReactComponent as Layers } from '@shared/ui-kit/assets/icons/layers.svg';
import { ReactComponent as LayoutGrid } from '@shared/ui-kit/assets/icons/layout-grid.svg';
import { ReactComponent as Leaf } from '@shared/ui-kit/assets/icons/leaf.svg';
import { ReactComponent as Lines } from '@shared/ui-kit/assets/icons/lines.svg';
import { ReactComponent as Link } from '@shared/ui-kit/assets/icons/link.svg';
import { ReactComponent as Lock } from '@shared/ui-kit/assets/icons/lock.svg';
import { ReactComponent as Mail } from '@shared/ui-kit/assets/icons/mail.svg';
import { ReactComponent as Menu } from '@shared/ui-kit/assets/icons/menu.svg';
import { ReactComponent as Message } from '@shared/ui-kit/assets/icons/message-circle.svg';
import { ReactComponent as Monitor } from '@shared/ui-kit/assets/icons/monitor.svg';
import { ReactComponent as MutualFunds } from '@shared/ui-kit/assets/icons/mutual-fund.svg';
import { ReactComponent as Notification } from '@shared/ui-kit/assets/icons/notification.svg';
import { ReactComponent as OpenLock } from '@shared/ui-kit/assets/icons/open-lock.svg';
import { ReactComponent as Package } from '@shared/ui-kit/assets/icons/package.svg';
import { ReactComponent as Palette } from '@shared/ui-kit/assets/icons/palette.svg';
import { ReactComponent as PartyPopper } from '@shared/ui-kit/assets/icons/party-popper.svg';
import { ReactComponent as Pen } from '@shared/ui-kit/assets/icons/pen.svg';
import { ReactComponent as PenTool } from '@shared/ui-kit/assets/icons/pen-tool.svg';
import { ReactComponent as Percent } from '@shared/ui-kit/assets/icons/percent.svg';
import { ReactComponent as Person } from '@shared/ui-kit/assets/icons/person.svg';
import { ReactComponent as Plug } from '@shared/ui-kit/assets/icons/plug.svg';
import { ReactComponent as PlugZap } from '@shared/ui-kit/assets/icons/plug-zap.svg';
import { ReactComponent as Plus } from '@shared/ui-kit/assets/icons/plus.svg';
import { ReactComponent as QrCode } from '@shared/ui-kit/assets/icons/qrcode.svg';
import { ReactComponent as Rebalance } from '@shared/ui-kit/assets/icons/rebalance.svg';
import { ReactComponent as Receipt } from '@shared/ui-kit/assets/icons/receipt.svg';
import { ReactComponent as Refresh } from '@shared/ui-kit/assets/icons/refresh.svg';
import { ReactComponent as RefreshTwoArrows } from '@shared/ui-kit/assets/icons/refresh-two-arrows.svg';
import { ReactComponent as Reporting } from '@shared/ui-kit/assets/icons/reporting.svg';
import { ReactComponent as Research } from '@shared/ui-kit/assets/icons/research.svg';
import { ReactComponent as Save } from '@shared/ui-kit/assets/icons/save.svg';
import { ReactComponent as Search } from '@shared/ui-kit/assets/icons/search.svg';
import { ReactComponent as Sell } from '@shared/ui-kit/assets/icons/sell.svg';
import { ReactComponent as Send } from '@shared/ui-kit/assets/icons/send.svg';
import { ReactComponent as Settings } from '@shared/ui-kit/assets/icons/settings.svg';
import { ReactComponent as Share } from '@shared/ui-kit/assets/icons/share.svg';
import { ReactComponent as Shield } from '@shared/ui-kit/assets/icons/shield.svg';
import { ReactComponent as ShieldCheck } from '@shared/ui-kit/assets/icons/shield-check.svg';
import { ReactComponent as Sheet } from '@shared/ui-kit/assets/icons/sheet.svg';
import { ReactComponent as ShoppingCart } from '@shared/ui-kit/assets/icons/shopping-cart.svg';
import { ReactComponent as Smartphone } from '@shared/ui-kit/assets/icons/smartphone.svg';
import { ReactComponent as Sort } from '@shared/ui-kit/assets/icons/sort.svg';
import { ReactComponent as SortAsc } from '@shared/ui-kit/assets/icons/sort-asc.svg';
import { ReactComponent as SortDesc } from '@shared/ui-kit/assets/icons/sort-desc.svg';
import { ReactComponent as SouthKoreaFlag } from '@shared/ui-kit/assets/icons/flags/south-korea.svg';
import { ReactComponent as SpainFlag } from '@shared/ui-kit/assets/icons/flags/spain.svg';
import { ReactComponent as Star } from '@shared/ui-kit/assets/icons/star.svg';
import { ReactComponent as Tag } from '@shared/ui-kit/assets/icons/tag.svg';
import { ReactComponent as Target } from '@shared/ui-kit/assets/icons/target.svg';
import { ReactComponent as TaxManager } from '@shared/ui-kit/assets/icons/tax-manager.svg';
import { ReactComponent as Trader } from '@shared/ui-kit/assets/icons/trader.svg';
import { ReactComponent as Trashcan } from '@shared/ui-kit/assets/icons/trashcan.svg';
import { ReactComponent as TrendingDown } from '@shared/ui-kit/assets/icons/trending-down.svg';
import { ReactComponent as TrendingUp } from '@shared/ui-kit/assets/icons/trending-up.svg';
import { ReactComponent as UKFlag } from '@shared/ui-kit/assets/icons/flags/uk.svg';
import { ReactComponent as Unlink } from '@shared/ui-kit/assets/icons/unlink.svg';
import { ReactComponent as Upload } from '@shared/ui-kit/assets/icons/upload.svg';
import { ReactComponent as USAFlag } from '@shared/ui-kit/assets/icons/flags/usa.svg';
import { ReactComponent as User } from '@shared/ui-kit/assets/icons/circle-user.svg';
import { ReactComponent as UserCheck } from '@shared/ui-kit/assets/icons/user-check.svg';
import { ReactComponent as UserPen } from '@shared/ui-kit/assets/icons/user-pen.svg';
import { ReactComponent as Users } from '@shared/ui-kit/assets/icons/users.svg';
import { ReactComponent as UserX } from '@shared/ui-kit/assets/icons/user-x.svg';
import { ReactComponent as Wallet } from '@shared/ui-kit/assets/icons/wallet.svg';
import { ReactComponent as Warning } from '@shared/ui-kit/assets/icons/warning.svg';
import { ReactComponent as Zap } from '@shared/ui-kit/assets/icons/zap.svg';
import { classNames } from '@shared/ui-kit/utils/classnames';

/** Type representing an SVG component. */
type SVGComponent = ComponentType<JSX.IntrinsicElements['svg']>;

const SVG_ICONS = {
  Account,
  Activity,
  Add,
  Advisors,
  AiBuilder,
  AlertTriangle,
  Analysis,
  ArrowDown,
  ArrowDownUp,
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  ArrowUp,
  Asset,
  AssetAllocation,
  Back,
  Boxes,
  BrazilFlag,
  Banknote,
  Briefcase,
  Building,
  Buy,
  Calendar,
  CanadaFlag,
  CaretDown,
  CaretRight,
  CaretUp,
  ChartColumn,
  ChartLine,
  ChartPie,
  ChatCaretLeft,
  ChatCaretRight,
  Checkbox,
  CheckCircle,
  Checked,
  ChevronDown,
  CircleArrowDown,
  CircleArrowUp,
  CircleCheck,
  ClipboardCheck,
  Clock,
  Close,
  CloseOutline,
  CopilotChatBackButton,
  CopilotChatButton,
  CopilotChatSendButton,
  Coins,
  Copy,
  CreditCard,
  Dashboard,
  DatePicker,
  DollarSign,
  Download,
  Eliminate,
  Ellipsis,
  EllipsisVertical,
  ETF,
  EuropeanUnionFlag,
  ExclamationMark,
  ExternalLink,
  Eye,
  EyeOff,
  FileCheck,
  FileImage,
  FilePen,
  FileText,
  Flame,
  FranceFlag,
  Funnel,
  GermanyFlag,
  Globe,
  HandCoins,
  Home,
  IdCard,
  Indeterminate,
  Info,
  Insights,
  JapanFlag,
  Landmark,
  Layers,
  LayoutGrid,
  Leaf,
  Lines,
  Link,
  Lock,
  Mail,
  Menu,
  Message,
  Monitor,
  MutualFunds,
  Notification,
  OpenLock,
  Package,
  Palette,
  PartyPopper,
  Pen,
  PenTool,
  Percent,
  Person,
  Plug,
  PlugZap,
  Plus,
  QrCode,
  Rebalance,
  Receipt,
  Refresh,
  RefreshTwoArrows,
  Reporting,
  Research,
  Save,
  Search,
  Sell,
  Send,
  Settings,
  Share,
  Shield,
  ShieldCheck,
  Sheet,
  ShoppingCart,
  Smartphone,
  Sort,
  SortAsc,
  SortDesc,
  SouthKoreaFlag,
  SpainFlag,
  Star,
  Tag,
  Target,
  TaxManager,
  Trader,
  Trashcan,
  TrendingDown,
  TrendingUp,
  UKFlag,
  Unlink,
  Upload,
  USAFlag,
  User,
  UserCheck,
  UserPen,
  Users,
  UserX,
  Wallet,
  Warning,
  Zap,
};

export type SvgIconName = keyof typeof SVG_ICONS;

/** Registry for storing and retrieving SVG components. */
export const ICON_REGISTRY = new Map<string, SVGComponent>(
  Object.entries(SVG_ICONS),
);

/**
 * Properties for the Icon component.
 *
 * @property title - The title of the icon.
 * @property icon - The name of the icon.
 * @property color - The color of the icon. It can be one of the following:
 *
 *   - Main: #1072BA
 *   - Secondary: #9C2B85
 *   - Success: #2DCC89
 *   - Danger: #E24646
 *   - Disabled: #9FADBE
 *   - Light: #FFFFFF
 *   - Dark: #323A58
 *   - None: unset
 *
 * @property size - The size of the icon. It can be one of the following:
 *
 *   - Sm: 9px
 *   - Md: 10.5px
 *   - Base: 12px
 *   - Lg: 15px
 *   - Xl: 18px
 *   - 2xl: 30px
 *   - Number: value in pixels
 */
export interface IconProps extends PropsWithoutRef<SVGProps<SVGSVGElement>> {
  title?: string;
  icon: SvgIconName;
  /**
   * The color of the icon. It can be one of the following:
   *
   * - Main: #1072BA
   * - Secondary: #3959E5
   * - Tertiary: #9C2B85
   * - Success: #2DCC89
   * - Danger: #E24646
   * - Disabled: #9FADBE
   * - Light: #FFFFFF
   * - Dark: #323A58
   * - Warning: #F9A825
   * - None: unset
   */
  color?:
    | 'main'
    | 'secondary'
    | 'tertiary'
    | 'success'
    | 'danger'
    | 'disabled'
    | 'light'
    | 'dark'
    | 'gray'
    | 'warning'
    | 'inherit';
  /**
   * The size of the icon. It can be one of the following:
   *
   * - Sm: 9px
   * - Md: 10.5px
   * - Base: 12px
   * - Lg: 15px
   * - Xl: 18px
   * - 2xl: 30px
   * - Number: value in pixels
   */
  size?: 'sm' | 'md' | 'base' | 'lg' | 'xl' | '2xl' | number;
  /**
   * Whether the icon should be filled (for icons that support it, like Star). When
   * true, the icon will be filled with the current color.
   */
  filled?: boolean;
}

export const classes = {
  color: {
    main: '!text-primary',
    secondary: '!text-secondary',
    tertiary: '!text-primary-pink',
    success: '!text-positive',
    danger: '!text-negative',
    disabled: '!text-sidebar-text',
    light: '!text-white',
    dark: '!text-sidebar-disabled',
    gray: '!text-primary-medium',
    warning: '!text-warning',
    inherit: '!text-inherit',
  },
  size: {
    sm: 'size-[0.75rem]',
    md: 'size-[0.875rem]',
    base: 'size-[1rem]',
    lg: 'size-[1.25rem]',
    xl: 'size-[1.5rem]',
    '2xl': 'size-[2.5rem]',
  },
};

/**
 * AppIcon is a forwardRef component that renders an icon from the ICON_REGISTRY. It
 * takes an icon name and SVG properties as props and returns an SVG element.
 *
 * @param props - The properties for the icon.
 * @param ref - The ref for the icon.
 * @returns An SVG element.
 * @throws Will throw an error if the icon is not found in the ICON_REGISTRY.
 */
export const AppIcon = forwardRef<SVGElement, IconProps>(
  ({ icon, size, color, className, filled, ...props }, ref) => {
    const Icon = useMemo(() => {
      if (!ICON_REGISTRY.has(icon)) {
        throw new Error(
          `The icon "${icon}" is not valid or not found in the registry.`,
        );
      }

      return ICON_REGISTRY.get(icon)!;
    }, [icon]);

    const iconClasses = useMemo(
      () =>
        classNames(
          classes.color?.[color!],
          typeof size !== 'number' && classes.size?.[size!],
          'transition-colors',
          className,
        ),
      [className, color, size],
    );

    const iconSize = useMemo(
      () =>
        typeof size === 'number'
          ? { width: `${size / 12}rem`, height: `${size / 12}rem` }
          : {},
      [size],
    );

    const filledProps = useMemo(() => {
      if (filled) {
        return {
          fill: 'currentColor',
          strokeWidth: 0,
        };
      }
      return {};
    }, [filled]);

    return (
      <Icon
        ref={ref as any}
        className={iconClasses}
        {...iconSize}
        {...filledProps}
        {...props}
      />
    );
  },
);

AppIcon.displayName = 'AppIcon';
